<template>
  <div
    ref="list"
    class="sp-list"
    role="listbox"
    :aria-activedescendant="focusedIndex"
    :aria-multiselectable="multiple"
    tabindex="-1"
  >
    <sp-list-subheader v-if="props.subheader" :title="props.subheader"></sp-list-subheader>

    <template v-for="(item, index) in items" :key="index">
      <sp-list-subheader v-if="isHeader(item)" :title="item.header"></sp-list-subheader>
      <sp-list-item
        v-else
        :id="indexOfListItem(index)"
        :tabindex="getItemTabindex(index)"
        :active="isSelected(item)"
        :active-prepend-icon="selectedPrependIcon"
        :active-prepend-icon-fill-color="selectedPrependIconFillColor"
        :aria-selected="focusedIndex === indexOfListItem(index)"
        :title="getItemTitle(item)"
        :subtitle="getItemSubtitle(item)"
        :tags="getItemTags(item)"
        :value="getItemValue(item)"
        :left-indentation="hasItemsWithPrependVisual"
        :right-indentation="hasItemsWithAppendIcon"
        :readonly="readonly"
        :wrap="wrap"
        v-bind="getItemProps(item)"
        :disabled="itemDisabledAttr(item)"
        :aria-disabled="itemAriaDisabled(item)"
        @click="handleItemClick(item)"
        @append-action="emit('append-action', item)"
      ></sp-list-item>
    </template>
  </div>
</template>

<script setup>
import { onKeyStroke, useFocus, useThrottleFn } from "@vueuse/core";
import { computed, ref, useTemplateRef, watch, onUnmounted } from "vue";
import { toArray, toBoolean } from "#ce/utils/props";
import { useExpose } from "#ce/composables/useExpose";
import { list as listProps } from "./props";
import { useSpList } from "./composables/useSpList";
import { useScrollSelectedIntoView } from "./composables/useScrollSelectedIntoView";

const emit = defineEmits(["update-selected", "select-item", "append-action"]);

const props = defineProps({
  ...listProps,
});

const {
  getItemProps,
  getItemTitle,
  getItemValue,
  getItemSubtitle,
  getItemTags,
  isHeader,
  isSelectable,
  isItemDisabled,
  indexOfListItem,
  listItemOfIndex,
  listItemIndexes,
} = useSpList(props);

const list = useTemplateRef("list");

/**
 * Check if the list has items with a prepend visual — an icon (`prependIcon` /
 * `prependIconUrl`) or an avatar (`prependAvatar`). If the selectedPrependIcon
 * prop is defined or any item has one, it will return true.
 *
 * This is used to determine if the list items should have left indentation, so
 * mixed lists (some rows with a prepend visual, some without) align consistently.
 */
const PREPEND_VISUAL_KEYS = ["prependIcon", "prependIconUrl", "prependAvatar"];
const hasItemsWithPrependVisual = computed(
  () =>
    props.selectedPrependIcon !== undefined ||
    props.items?.some((item) => {
      const itemProps = getItemProps(item);
      // Truthy, not just present: SpListItem renders a visual only for a
      // non-empty value, so `prependIconUrl: ""` must not trigger indentation.
      return PREPEND_VISUAL_KEYS.some((key) => Boolean(itemProps[key]));
    }),
);

/**
 * Check if the list has items with an append icon or an append caret.
 * If any item has either, it will return true.
 *
 * This is used to determine if the list items should have right indentation.
 */
const hasItemsWithAppendIcon = computed(() =>
  props.items?.some((item) => {
    const itemProps = getItemProps(item);
    return "appendIcon" in itemProps || toBoolean(itemProps.appendCaret);
  }),
);

/**
 * If `listenOnKeyEvent` is true, the list will listen to key events and navigate through the list.
 * If `listenOnKeyEvent` is "focus", the list will listen to key events only when it is focused.
 * Otherwise, it will not listen to key events and all existing key event listeners will be removed.
 */
const { focused } = useFocus(list);
const listenOnKeyEvent = computed(
  () => toBoolean(props.listenOnKeyEvent) || (props.listenOnKeyEvent === "focus" && focused.value),
);

/**
 * The key stroke listeners for navigation and selection.
 * These listeners will be removed when the component is unmounted or when `listenOnKeyEvent` is false.
 */
let navigationKeyStrokeListener;
let selectionKeyStrokeListener;

const selectionKeys = ["Enter", " "];
const navigationKeys = ["ArrowDown", "ArrowUp", "Home", "End"];

watch(
  listenOnKeyEvent,
  (value) => {
    if (value) {
      selectionKeyStrokeListener = onKeyStroke(selectionKeys, handleSelectionKeyStroke, { dedupe: true });
      navigationKeyStrokeListener = onKeyStroke(navigationKeys, handleNavigationKeyStroke);
    } else {
      selectionKeyStrokeListener?.();
      navigationKeyStrokeListener?.();
    }
  },
  { immediate: true },
);

// Ensure listeners are cleaned up on unmount
onUnmounted(() => {
  selectionKeyStrokeListener?.();
  navigationKeyStrokeListener?.();
});

// Public keyboard entry point: move roving focus to the first / last navigable
// option. Focuses the DOM row explicitly so it applies even when the index is
// already at the target (the watcher below only fires on a changed index).
const { exposeMethods } = useExpose();
exposeMethods({
  focusItem(location) {
    const previousIndex = focusedIndex.value;
    focus(location === "last" ? "End" : "Home");
    // The focusedIndex watcher already focuses the row when the index changes;
    // only drive it here when the index is unchanged (watcher won't re-run).
    if (focusedIndex.value === previousIndex) {
      focusRow(focusedIndex.value);
    }
  },
});

function getItemTabindex(index) {
  return focusedIndex.value === indexOfListItem(index) ? 0 : -1;
}

// Reflect aria-disabled onto the focusable `sp-list-item` host (not just the
// inner shadow-DOM node), and omit it entirely for enabled rows so they carry
// no `aria-disabled="false"`.
function itemAriaDisabled(item) {
  return isItemDisabled(item) ? "true" : undefined;
}

// Bind a normalized boolean so the host `[disabled]` attribute matches
// `isItemDisabled`/`isSelectable`, and omit it entirely for enabled rows so
// they carry no `disabled="false"`.
function itemDisabledAttr(item) {
  return isItemDisabled(item) ? true : undefined;
}

// The selection-key listener is window-level, so Space/Enter typed into a
// text-entry element (e.g. an external search field driving this list) must
// reach that field instead of being cancelled as a selection keystroke.
// Keyboard selection is unaffected: arrow-key navigation moves real focus
// onto a list row first, and a row is not text entry.
function originatesFromTextEntry(e) {
  const origin = e.composedPath?.()[0] ?? e.target;
  return (
    origin instanceof HTMLElement &&
    (origin.tagName === "INPUT" || origin.tagName === "TEXTAREA" || origin.isContentEditable)
  );
}

function handleSelectionKeyStroke(e) {
  if (originatesFromTextEntry(e)) {
    return;
  }

  e.stopPropagation();
  e.preventDefault();
  return conditionallyUpdateSelection();
}

// A held arrow key repeats at the OS key-repeat rate, which users can set as
// fast as ~30 rows a second — quicker than the eye can follow or the hand can
// release on a target. Auto-repeat runs at a fixed cadence instead; a discrete
// press stays immediate, so deliberate taps are never dropped.
const REPEATED_NAVIGATION_INTERVAL_MS = 80;
const focusOnRepeat = useThrottleFn(focus, REPEATED_NAVIGATION_INTERVAL_MS);

function handleNavigationKeyStroke(e) {
  e.preventDefault();
  e.stopPropagation();

  if (e.repeat) {
    focusOnRepeat(e.key);
  } else {
    focus(e.key);
  }
}

useScrollSelectedIntoView(list, () => toBoolean(props.scrollSelectedIntoView));

const focusedIndex = ref(-1);

// Move real DOM focus (and scroll) to the row at `index`. Driven by the
// focusedIndex watcher and called directly by focusItem for the unchanged-index case.
function focusRow(index) {
  if (index < 0) {
    return;
  }
  const el = list.value?.querySelector(`sp-list-item[id='${index}']`);
  const behavior = index === 0 || index === listItemIndexes.value.length - 1 ? "auto" : "smooth";
  el?.focus();
  el?.scrollIntoView({ block: "nearest", inline: "nearest", behavior });
}

watch(focusedIndex, focusRow);

/**
 * Focus the list item based on the key.
 *
 * If the key is "Home", the first item will be focused.
 * If the key is "End", the last item will be focused.
 * If the key is "ArrowDown", the next item will be focused. If the last item is focused, the first item will be focused.
 * If the key is "ArrowUp", the previous item will be focused. If the first item is focused, the last item will be focused.
 *
 * @param {KeyboardEvent} e
 * @param {string} key
 */
function focus(key) {
  const numberOfItems = listItemIndexes.value.length;

  if (numberOfItems === 0) {
    focusedIndex.value = -1;
    return;
  }

  if (key === "Home") {
    focusedIndex.value = findNavigableIndex(0, 1);
  } else if (key === "End") {
    focusedIndex.value = findNavigableIndex(numberOfItems - 1, -1);
  } else if (key === "ArrowDown") {
    focusedIndex.value = findNavigableIndex(focusedIndex.value + 1, 1);
  } else if (key === "ArrowUp") {
    // When nothing is focused yet (focusedIndex === -1), ArrowUp wraps to the
    // last item — so start the search from the last index rather than -2.
    const from = focusedIndex.value <= 0 ? numberOfItems - 1 : focusedIndex.value - 1;
    focusedIndex.value = findNavigableIndex(from, -1);
  }
}

/**
 * Starting at `from`, walk in the `step` direction (with wrap-around) and
 * return the first navigation index whose item is not disabled, so keyboard
 * navigation lands only on actionable rows. Returns `-1` when every row is
 * disabled (no navigable target).
 *
 * @param {number} from - The navigation index to start searching from.
 * @param {number} step - The direction to walk in (+1 forward, -1 backward).
 * @returns {number} The first non-disabled navigation index, or -1 if none.
 */
function findNavigableIndex(from, step) {
  const numberOfItems = listItemIndexes.value.length;

  for (let offset = 0; offset < numberOfItems; offset++) {
    const index = (((from + step * offset) % numberOfItems) + numberOfItems) % numberOfItems;
    if (!isItemDisabled(listItemOfIndex(index))) {
      return index;
    }
  }

  return -1;
}

const multiple = computed(() => toBoolean(props.multiple));
const required = computed(() => toBoolean(props.required));

const model = ref(props.selected ? toArray(props.selected) : []);
watch(
  () => props.selected,
  (value) => (model.value = toArray(value)),
);

// Reports the selection the visitor just made, synchronously with the click or keypress that made
// it. Every write reassigns the ref, so an unchanged reference means the item was not selectable
// and there is nothing to report. The `selected` prop sync above writes the model without coming
// through here, so it stays silent.
function selectByUser(item) {
  const reported = model.value;

  updateSelection(item);

  if (model.value !== reported) {
    emit("update-selected", model.value);
  }
}

function conditionallyUpdateSelection() {
  if (focusedIndex.value >= 0) {
    const item = listItemOfIndex(focusedIndex.value);
    selectByUser(item);
    emit("select-item", item);
  }
}

function isSelected(item) {
  if (!isSelectable(item)) {
    return false;
  }

  const value = getItemValue(item);
  return model.value.some((selected) => isItemValue(selected, value));
}

function handleItemClick(item) {
  emit("select-item", item);
  selectByUser(item);
}

function updateSelection(item) {
  if (!isSelectable(item)) {
    return;
  }

  toggleSelection(item);
}

/**
 * Toggle the selection of the item.
 *
 * If the multiple prop is true, the item will be added to the selection if it is not selected.
 * If the multiple prop is true, the item will be removed from the selection if it is selected.
 *
 * If the multiple prop is false, the item will be selected if it is not selected.
 * If the multiple prop is false, the item will be unselected if it is selected.
 *
 * @param {Object} item
 */
function toggleSelection(item) {
  if (multiple.value) {
    toggleMultipleSelection(item);
  } else {
    toggleSingeSelection(item);
  }
}

function toggleMultipleSelection(item) {
  const value = getItemValue(item);
  const index = model.value.findIndex((entry) => isItemValue(value, entry));

  // Reassign the ref with a new array so the shallow `watch(model, ...)` above
  // fires and `update-selected` reaches the consumer. In-place push / splice
  // mutate the same array reference, and the watcher doesn't observe that.
  if (index === -1) {
    model.value = [...model.value, value];
  } else if (!(required.value && model.value.length === 1)) {
    // Keep the last remaining selection when required so the list is never empty.
    model.value = model.value.filter((_, i) => i !== index);
  }
}

function toggleSingeSelection(item) {
  const value = getItemValue(item);
  if (isItemValue(model.value?.[0], value)) {
    // Re-selecting the current item clears it, unless a selection is required.
    if (!required.value) {
      model.value = [];
    }
  } else {
    model.value = [value];
  }
}

function isItemValue(value, other) {
  return String(other) === String(value);
}
</script>

<style>
:host {
  display: block;
  --list-min-width: var(--sp-comp-list-min-width, auto);
}
</style>

<style scoped lang="scss">
.sp-list {
  min-width: var(--list-min-width);
  width: 100%;
}

sp-list-item:hover:not([active]) + sp-list-item,
sp-list-item:focus:not([active]) + sp-list-item,
sp-list-item:focus-visible:not([active]) + sp-list-item {
  --divider-width: 100%;
  --sp-comp-divider-color: var(--sp-comp-list-item-hover-background-color, #ccc);
  --divider-left: 0;
}

/* Same divider blend for the row after a highlighted row, but chained through
   the highlighted token first so a theme that sets it apart from hover keeps a
   matching divider. */
sp-list-item[highlighted]:not([active]) + sp-list-item {
  --divider-width: 100%;
  --sp-comp-divider-color: var(
    --sp-comp-list-item-highlighted-background-color,
    var(--sp-comp-list-item-hover-background-color, #ccc)
  );
  --divider-left: 0;
}

sp-list-subheader + sp-list-item,
sp-list-item:first-child {
  --display-divider: none;
}
sp-list-item[active] + sp-list-item {
  --display-divider: var(
    --sp-comp-list-item-active-display-next-divider,
    var(--sp-comp-list-item-display-divider, block)
  );
}
</style>
